/*
 * Copyright (c) 2021. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React, {FunctionComponent, useEffect, useState} from "react";
import _includes from "lodash/includes";
import Paper from "@mui/material/Paper";
import {useForm} from "../../../../provider/Form/FormProvider";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {fetchCollection, queryKeys} from "../../../../services/reactQuery/reactQueryService";
import LegalbirdLoader from "../../../ContentLoader/LegalbirdLoader";
import _get from "lodash/get";
import {getProductData} from "../../../../services/productService";
import {AbstractCase} from "../../../../types/Entities/AbstractCase";
import FormSubmitProps from "../../../../types/FormSubmitProps";
import {ContractDocumentsProps} from "../../../../types/ContractDocuments";
import wizardStyle from "../../wizardStyle";
import useMediaObjectCombinationRequest from "../../../../hooks/useMediaObjectCombinationRequest";

type ContractDocumentsWrapperProps = {
  product: AbstractCase;
  submitProps: FormSubmitProps;
  DocumentsFormComponent: FunctionComponent<ContractDocumentsProps>;
};

export default function ContractDocumentsWrapper({ product, submitProps, DocumentsFormComponent }: ContractDocumentsWrapperProps) {
  const mediaObjectCombinationRequestTypes = ["contractDocument"];
  const { mediaObjectCombinationRequests, isLoadingMediaObjectCombinationRequest, updateAction } = useMediaObjectCombinationRequest(
    product,
    mediaObjectCombinationRequestTypes
  );

  const mediaObjectCombinationRequest = mediaObjectCombinationRequests.contractDocument;

  const queryClient = useQueryClient();
  const [contractUploadBlocked, setContractUploadBlocked] = useState(false);
  const [caseSpecificSubmitDisabled, setCaseSpecificSubmitDisabled] = useState(false);
  const productApiUri = getProductData(product.name, "apiUri");
  const { values, handleChange } = useForm();
  const urlParams = {
    product: product.name,
    productId: product.id,
  };
  const { data: mediaObjectCollection, isLoading } = useQuery(queryKeys.collection("/media_objects", urlParams), () =>
    fetchCollection("/media_objects", urlParams)
  );
  const refreshCase = async () => {
    await queryClient.invalidateQueries(queryKeys.item(productApiUri, product.id));
  };
  const refreshMediaObjectCollection = () => queryClient.invalidateQueries(queryKeys.collection("/media_objects", urlParams));

  const isContractDocumentRequired = _includes(["enforceClaim", "defendClaim"], product.caseCategory);

  const contractDocumentCollectionLength = _get(mediaObjectCombinationRequest, ["filesToCombine", 0, "files"], []).length;
  const submitIsDisabled =
    (!values.clientHasNoContractDocument && isContractDocumentRequired && contractDocumentCollectionLength === 0) || caseSpecificSubmitDisabled;

  useEffect(() => {
    if (values.clientHasNoContractDocument === true && contractDocumentCollectionLength >= 1) {
      handleChange({
        target: {
          type: "checkbox",
          name: "clientHasNoContractDocument",
          value: false,
        },
      });
    }
  }, [contractDocumentCollectionLength]);

  if (isLoading || isLoadingMediaObjectCombinationRequest) {
    return <LegalbirdLoader centered />;
  }

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <DocumentsFormComponent
        mediaObjectCombinationRequest={mediaObjectCombinationRequest}
        product={product}
        submitProps={submitProps}
        isContractDocumentRequired={isContractDocumentRequired}
        contractUploadBlocked={contractUploadBlocked}
        setContractUploadBlocked={setContractUploadBlocked}
        refreshCase={refreshCase}
        showContractDocumentRequiredWarning={isContractDocumentRequired && contractDocumentCollectionLength < 1}
        refreshMediaObjectCollection={refreshMediaObjectCollection}
        mediaObjectCollection={mediaObjectCollection}
        updateAction={updateAction}
        submitIsDisabled={submitIsDisabled}
        setCaseSpecificSubmitDisabled={setCaseSpecificSubmitDisabled}
      />
    </Paper>
  );
}
